import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ACTION_REFRESH} from 'notebook/store/VersioningConstants';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {
  StructuredTable,
  StructuredTableKind,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {handleRefreshLookupStructuredTable} from 'notebook/tables/refresh-table-data/RefreshLookupColumnData';
import {handleRefreshStructuredTable} from 'notebook/tables/refresh-table-data/RefreshTableData';
import {
  REFRESH_ALL_OPTION,
  REFRESH_LOOKUP_OPTION,
  REFRESH_LOOKUP_COLUMN_OPTION,
  REFRESH_TABLE_LABEL,
  RESYNC_TABLE_LABEL,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {performClientSideWriteAction} from 'notebook/util/PerformActionFunctions';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';

import rowHasUnsubmittedChanges from '../util/rowHasUnsubmittedChanges';

export const FORMULA_PRESENT_WARNING = 'Formulas in submitted rows will be overwritten';
export const UNSUBMITTED_CHANGES_IN_SUBMITTED_ROWS_WARNING = 'Changes to submitted rows will be lost';
export const EDITS_IN_UNSUBMITTED_ROWS_WARNING = 'Edits in unsubmitted rows may be lost';

export function RefreshWarningModal({
  warnings,
  onRequestClose,
  onRequestSubmit,
}: {
  warnings: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}): JSX.Element {
  const warningContent = warnings.map((warning) => <li key={warning}>{warning}</li>);

  return (
    <Modal
      title="Are you sure you'd like to continue?"
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {text: 'Yes, continue', submit: true},
        {text: 'Cancel', cancel: true},
      ]}
    >
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text="Re-syncing this table will modify data:"
        supportingInfo={<ul>{warningContent}</ul>}
      />
    </Modal>
  );
}

function onInvokeRefreshAllAction(
  props: Pick<
    TableActionFunctionParameters<{}, {}>,
    'entryOrTemplateId' | 'structuredTableAdapter' | 'structuredTable' | 'computedTable' | 'updateHandler'
  >
): void {
  const {entryOrTemplateId, structuredTableAdapter, structuredTable, computedTable, updateHandler} = props;
  assert(entryOrTemplateId != null, 'RefreshTable action cannot be invoked outside a notebook entry');

  const doRefresh = async (): Promise<void> => {
    await handleRefreshStructuredTable({
      entryOrTemplateId,
      structuredTableAdapter,
      structuredTable,
      computedTable,
      onUpdateTable: updateHandler,
    });
  };

  performClientSideWriteAction({
    entryOrTemplateId,
    loadingScreenMessage: BenchlingConfig.ELN_ENABLE_RESYNC_TABLE_LABEL ? 'Re-syncing' : 'Refreshing...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: doRefresh,
    analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_STRUCTURED_TABLE,
    analyticsMetadata: {structuredTableKind: structuredTable.kind},
  });
}

function onInvokeRefreshLookupColumnsAction(
  props: Pick<TableActionFunctionParameters<{}, {}>, 'entryOrTemplateId' | 'structuredTable'>
): void {
  const {entryOrTemplateId, structuredTable} = props;
  assert(
    entryOrTemplateId != null,
    'RefreshTable action for lookup columns cannot be invoked outside a notebook entry'
  );

  const doRefresh = (): Promise<void> => {
    handleRefreshLookupStructuredTable({
      entryOrTemplateId,
      structuredTable,
    });
    return Promise.resolve();
  };
  performClientSideWriteAction({
    entryOrTemplateId,
    loadingScreenMessage: 'Looking up new data...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: doRefresh,
    analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_STRUCTURED_TABLE_LOOKUP_COLUMNS,
    analyticsMetadata: {structuredTableKind: structuredTable.kind},
  });
}

export function addTableRefreshWarnings(
  structuredTable: StructuredTable<unknown, unknown>,
  computedTable: Array<Array<EvaluatedCell>>
): [
  unsubmittedChangesInSubmittedRows: boolean,
  possibleEditsInUnsubmittedRows: boolean,
  formulaPresent: boolean,
] {
  let unsubmittedChangesInSubmittedRow = false;
  let possibleEditsInUnsubmittedRows = false;
  let formulaPresent = false;
  for (let rowIndex = 0; rowIndex < structuredTable.rows.length; rowIndex++) {
    if (structuredTable.rows[rowIndex].isFinalized) {
      if (
        rowHasUnsubmittedChanges(
          computedTable[rowIndex],
          structuredTable.rows[rowIndex],
          structuredTable.columns,
          structuredTable.schemas
        )
      ) {
        unsubmittedChangesInSubmittedRow = true;
      }
      for (let columnIndex = 0; columnIndex < structuredTable.rows[rowIndex].cells.length; columnIndex++) {
        if (TableUtil.isCellFormula(structuredTable.rows[rowIndex].cells[columnIndex])) {
          formulaPresent = true;
        }
      }
    } else if (
      [StructuredTableKind.INVENTORY, StructuredTableKind.MIXTURE_INGREDIENTS].includes(structuredTable.kind)
    ) {
      // Inventory tables may have unsubmitted rows with cells that are both editable and auto-populated,
      // so we show a warning that edits may be overwritten by re-sync.
      // Mixture Prep Ingredients tables have all unsubmitted rows deleted during re-sync.
      possibleEditsInUnsubmittedRows = true;
    }
    if (unsubmittedChangesInSubmittedRow && formulaPresent) {
      break;
    }
  }
  return [unsubmittedChangesInSubmittedRow, possibleEditsInUnsubmittedRows, formulaPresent];
}

function RefreshTableButton({params}: {params: TableActionFunctionParameters<{}, {}>}): JSX.Element {
  const {entryOrTemplateId, structuredTableAdapter, structuredTable, computedTable, updateHandler, locked} =
    params;
  const warnings: Array<string> = useMemo(() => {
    const tempWarnings = [];
    if (BenchlingConfig.ELN_ENABLE_RESYNC_TABLE_LABEL) {
      const [unsubmittedChangesInSubmittedRow, possibleEditsInUnsubmittedRows, formulaPresent] =
        addTableRefreshWarnings(structuredTable, computedTable);

      if (unsubmittedChangesInSubmittedRow) {
        tempWarnings.push(UNSUBMITTED_CHANGES_IN_SUBMITTED_ROWS_WARNING);
      }
      if (possibleEditsInUnsubmittedRows) {
        tempWarnings.push(EDITS_IN_UNSUBMITTED_ROWS_WARNING);
      }
      if (formulaPresent) {
        tempWarnings.push(FORMULA_PRESENT_WARNING);
      }
    }
    return tempWarnings;
  }, [computedTable, structuredTable]);

  const refreshAllAction = useCallback(() => {
    if (BenchlingConfig.ELN_ENABLE_RESYNC_TABLE_LABEL && warnings.length) {
      runModal(RefreshWarningModal, {
        warnings,
        onRequestSubmit: () => {
          onInvokeRefreshAllAction({
            entryOrTemplateId,
            structuredTableAdapter,
            structuredTable,
            computedTable,
            updateHandler,
          });
        },
      });
    } else {
      onInvokeRefreshAllAction({
        entryOrTemplateId,
        structuredTableAdapter,
        structuredTable,
        computedTable,
        updateHandler,
      });
    }
  }, [entryOrTemplateId, structuredTableAdapter, structuredTable, computedTable, updateHandler, warnings]);

  const refreshLookupColumnsAction = useCallback(() => {
    onInvokeRefreshLookupColumnsAction({entryOrTemplateId, structuredTable});
  }, [entryOrTemplateId, structuredTable]);

  const isRefreshLookupDisabled = useMemo(
    () => locked || !structuredTable.columns.some((c) => c.columnType === TableColumnType.DEDICATED_LOOKUP),
    [locked, structuredTable.columns]
  );

  return (
    <Tooltip title={BenchlingConfig.ELN_ENABLE_RESYNC_TABLE_LABEL ? RESYNC_TABLE_LABEL : REFRESH_TABLE_LABEL}>
      <Dropdown
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
            <Option
              key={`refresh-lookup-columns-${structuredTable.provenanceUuid}`}
              isDisabled={isRefreshLookupDisabled}
              onSelect={refreshLookupColumnsAction}
              shouldEllipsize={true}
            >
              {BenchlingConfig.ELN_ENABLE_RESYNC_TABLE_LABEL
                ? REFRESH_LOOKUP_COLUMN_OPTION
                : REFRESH_LOOKUP_OPTION}
            </Option>
            <Option
              key={`refresh-all-${structuredTable.provenanceUuid}`}
              isDisabled={locked}
              onSelect={refreshAllAction}
              shouldEllipsize={true}
            >
              {BenchlingConfig.ELN_ENABLE_RESYNC_TABLE_LABEL ? RESYNC_TABLE_LABEL : REFRESH_ALL_OPTION}
            </Option>
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className="u-mini-spacing-left"
          disabled={locked}
          icon="assembly"
          iconAltText={
            BenchlingConfig.ELN_ENABLE_RESYNC_TABLE_LABEL ? RESYNC_TABLE_LABEL : REFRESH_TABLE_LABEL
          }
          isDropdown={true}
          dataTestAttrs={{
            component: 'RefreshTableButton',
            element: 'button',
          }}
        />
      </Dropdown>
    </Tooltip>
  );
}

export function renderDropdownButton<TTableMetadata, TTableRowMetadata>(
  props: TableActionFunctionParameters<TTableMetadata, TTableRowMetadata>
): JSX.Element {
  return <RefreshTableButton params={props as TableActionFunctionParameters<{}, {}>} />;
}

/**
 * An ActionFunction that refreshes the contents of a table.
 *
 * If the entry the table is contained in is locked, then the button
 * to refresh is disabled.
 */

export const refreshTableActionFunction = makeCustomButtonActionFunction({
  renderToolbarButton: renderDropdownButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});
